/**
 * Umgang mit voruebergehenden Stoerungen des KI-Dienstes.
 *
 * Google meldet Lastspitzen als eigenen Fehler ("This model is currently
 * experiencing high demand"). Bisher reichte der gemini-proxy das als HTTP 400
 * durch, der Client warf sofort, und ein 40-seitiges Leistungsverzeichnis war
 * wegen eines Aussetzers von zwei Sekunden verloren — samt der Minute, die das
 * Hochladen und Lesen gekostet hat.
 *
 * Zwei Dinge gehoeren deshalb getrennt beurteilt:
 *  - VORUEBERGEHEND (Ueberlastung, Ratenbegrenzung, 5xx): noch einmal
 *    versuchen, mit wachsendem Abstand.
 *  - DAUERHAFT (falsche Anfrage, fehlende Berechtigung, aufgebrauchtes
 *    Kontingent): sofort aufgeben. Wiederholen kostet hier nur Zeit und
 *    verschleiert die Ursache.
 *
 * Rein und ohne Netzwerk, damit die Einstufung ohne echten API-Aufruf
 * pruefbar bleibt.
 */

/**
 * Formulierungen, mit denen der Dienst eine Lastspitze meldet. Bewusst als
 * Textmuster und nicht nur ueber den HTTP-Status: der Proxy verpackt jeden
 * Fehler als 400, der eigentliche Status geht dabei verloren.
 */
const TRANSIENT_PATTERNS = [
    'high demand',
    'overloaded',
    'unavailable',
    'try again later',
    'temporarily',
    'timeout',
    'timed out',
    'deadline exceeded',
    'rate limit',
    'too many requests',
    'internal error',
];

/**
 * Formulierungen, die NIE voruebergehend sind. Sie stehen vor der
 * Transient-Pruefung, weil "quota exceeded" sonst ueber "rate limit"
 * mitgefangen wuerde und die App minutenlang gegen eine leere Abrechnung
 * anrennt.
 */
const PERMANENT_PATTERNS = [
    'quota',
    'billing',
    'api key',
    'permission',
    'not found',
    'unbekannte action',
    'invalid',
];

const lower = (s: unknown): string => (typeof s === 'string' ? s : '').toLowerCase();

/**
 * Lohnt ein weiterer Versuch?
 *
 * @param message Fehlertext, wie ihn der Proxy durchreicht.
 * @param status  HTTP-Status, sofern bekannt.
 */
export const isTransientAiError = (message: unknown, status?: number): boolean => {
    const text = lower(message);
    if (PERMANENT_PATTERNS.some((p) => text.includes(p))) return false;
    if (TRANSIENT_PATTERNS.some((p) => text.includes(p))) return true;
    // 429 = Ratenbegrenzung, 5xx = Stoerung auf der Gegenseite. 400 nicht:
    // das ist die eigene Anfrage.
    return status === 429 || (status !== undefined && status >= 500 && status < 600);
};

/**
 * Wartezeiten zwischen den Versuchen in Millisekunden. Die Laenge des Feldes
 * bestimmt die Zahl der WIEDERHOLUNGEN (der erste Versuch zaehlt nicht mit).
 *
 * Bewusst im Sekundenbereich: eine Lastspitze bei Google dauert selten
 * Millisekunden, und der Nutzer sieht ohnehin "KI analysiert …". Mehr als drei
 * Versuche waeren dagegen eine Zumutung — dann ist der Dienst wirklich weg.
 */
export const AI_RETRY_DELAYS_MS = [1500, 4000];

/**
 * Fehlertext fuer den Nutzer. Deutsch, und er sagt, was jetzt zu tun ist.
 *
 * Die rohe Meldung bleibt angehaengt, damit im Zweifel nachvollziehbar ist,
 * was der Dienst tatsaechlich gesagt hat.
 */
export const describeAiError = (message: unknown, attempts: number): string => {
    const raw = typeof message === 'string' && message.trim() ? message.trim() : 'Unbekannter Fehler';
    const text = lower(raw);

    if (text.includes('quota') || text.includes('billing')) {
        return 'Das KI-Kontingent ist aufgebraucht. Bitte die Abrechnung im Google-Konto prüfen. '
            + `(Meldung des Dienstes: ${raw})`;
    }
    if (text.includes('api key') || text.includes('permission')) {
        return 'Der KI-Dienst hat den Zugang abgelehnt — vermutlich ist der Schlüssel abgelaufen '
            + `oder falsch hinterlegt. (Meldung des Dienstes: ${raw})`;
    }
    if (isTransientAiError(raw)) {
        const tries = attempts === 1 ? 'Ein Versuch' : `${attempts} Versuche`;
        return 'Der KI-Dienst ist gerade überlastet. Das ist vorübergehend und liegt nicht an dieser App — '
            + `bitte in ein bis zwei Minuten noch einmal versuchen. (${tries} unternommen.)`;
    }
    return raw;
};

/**
 * Fuehrt `attempt` aus und wiederholt bei voruebergehenden Stoerungen.
 *
 * @param attempt Der Aufruf. Bekommt die laufende Versuchsnummer (ab 1).
 * @param opts.sleep Wartefunktion — im Test durch eine sofort erfuellte
 *   Zusage ersetzbar, damit die Faelle ohne echte Wartezeit pruefbar sind.
 */
export const withAiRetry = async <T>(
    attempt: (tryNumber: number) => Promise<T>,
    opts: {
        delays?: number[];
        sleep?: (ms: number) => Promise<void>;
        onRetry?: (tryNumber: number, waitMs: number, error: unknown) => void;
    } = {},
): Promise<T> => {
    const delays = opts.delays ?? AI_RETRY_DELAYS_MS;
    const sleep = opts.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));

    let lastError: unknown;
    for (let i = 0; i <= delays.length; i++) {
        try {
            return await attempt(i + 1);
        } catch (e) {
            lastError = e;
            const status = (e as { status?: number })?.status;
            const message = (e as Error)?.message;
            const isLast = i === delays.length;
            if (isLast || !isTransientAiError(message, status)) throw e;
            opts.onRetry?.(i + 1, delays[i], e);
            await sleep(delays[i]);
        }
    }
    throw lastError;
};
